<template>
	<div>
		<v-dialog v-model="dialogOpen" max-width="1240" persistent scrollable>
			<v-card class="financeiro-dialog" outlined>
				<v-card-title class="financeiro-dialog__title">
					<span>Importar múltiplos lançamentos</span>
					<v-spacer />
					<v-btn icon :disabled="saving" @click="fecharDialog">
						<v-icon>mdi-close</v-icon>
					</v-btn>
				</v-card-title>

				<v-divider />

				<v-card-text class="pt-6 pb-0">
					<div class="financeiro-info-box mb-6">
						Use esta funcionalidade para importar vários lançamentos financeiros de uma só vez por arquivo Excel (.xlsx). O arquivo deve conter os dados base do lançamento, e os campos tipo, categoria e conta bancária serão preenchidos manualmente aqui no sistema antes de salvar.
					</div>

					<div class="financeiro-multiplos__actions mb-6">
						<v-file-input
							v-model="arquivo"
							class="financeiro-multiplos__upload"
							accept=".xlsx"
							label="Upload do arquivo .xlsx"
							prepend-icon="mdi-file-excel-outline"
							outlined
							dense
							show-size
							truncate-length="28"
							:disabled="saving"
							@change="handleArquivoSelecionado"
						/>

						<v-btn class="financeiro-secondary-btn" outlined :disabled="saving" @click="baixarModeloArquivo">
							<v-icon left size="18">mdi-download</v-icon>
							Baixar modelo do arquivo
						</v-btn>
					</div>

					<div v-if="linhasImportadas.length" class="financeiro-multiplos__content">
						<div class="financeiro-multiplos__summary mb-3">
							<div class="financeiro-multiplos__title">Lançamentos encontrados</div>
							<div class="financeiro-multiplos__subtitle">
								Preencha manualmente tipo, categoria e conta bancária antes de salvar.
							</div>
						</div>

						<v-alert v-if="invalidRows.length" dense outlined type="warning" class="mb-4">
							Existem {{ invalidRows.length }} linha(s) com campos obrigatórios pendentes. Preencha descrição, valor, tipo e categoria para liberar o salvamento.
						</v-alert>

						<v-data-table
							class="financeiro-table"
							:headers="headers"
							:items="linhasImportadas"
							:item-key="'__id'"
							:disable-pagination="true"
							:hide-default-footer="true"
						>
							<template v-slot:[`item.descricao`]='{ item }'>
								<v-text-field
									v-model.trim="item.descricao"
									dense
									outlined
									hide-details
									:class="fieldErrorClass(item, 'descricao')"
								/>
							</template>

							<template v-slot:[`item.valor`]='{ item }'>
								<v-text-field
									v-model="item.valor"
									dense
									outlined
									hide-details
									prefix="R$"
									:class="fieldErrorClass(item, 'valor')"
									@input="onValorInput(item, $event)"
								/>
							</template>

							<template v-slot:[`item.data_prevista`]='{ item }'>
								<v-text-field
									v-model="item.data_prevista"
									type="date"
									dense
									outlined
									hide-details
								/>
							</template>

							<template v-slot:[`item.tipo`]='{ item }'>
								<v-select
									v-model="item.tipo"
									:items="tiposLancamento"
									dense
									outlined
									hide-details
									:class="fieldErrorClass(item, 'tipo')"
								/>
							</template>

							<template v-slot:[`item.categoria`]='{ item }'>
								<v-combobox
									v-model.trim="item.categoria"
									:items="categorias"
									dense
									outlined
									hide-details
									:class="fieldErrorClass(item, 'categoria')"
								/>
							</template>

							<template v-slot:[`item.conta_bancaria_id`]='{ item }'>
								<v-select
									v-model="item.conta_bancaria_id"
									:items="contasBancarias"
									item-text="nome"
									item-value="id"
									dense
									outlined
									hide-details
									clearable
									no-data-text="Conta nao cadastrada"
								/>
							</template>

							<template v-slot:[`item.veiculo_ids`]='{ item }'>
								<v-autocomplete
									v-model="item.veiculo_ids"
									:items="veiculosOptions"
									item-text="label"
									item-value="value"
									dense
									outlined
									hide-details
									multiple
									small-chips
									deletable-chips
									clearable
									no-data-text="Veiculo nao cadastrado"
								/>
							</template>

							<template v-slot:[`item.observacoes`]='{ item }'>
								<v-text-field
									v-model.trim="item.observacoes"
									dense
									outlined
									hide-details
								/>
							</template>

							<template v-slot:[`item.erros`]='{ item }'>
								<div class="financeiro-multiplos__errors">
									<span v-for="erro in getRowErrors(item)" :key="erro" class="financeiro-error-chip">
										{{ erro }}
									</span>
								</div>
							</template>
						</v-data-table>
					</div>

					<div v-else class="financeiro-empty-state">
						Nenhum lançamento importado ainda. Faça o upload de um arquivo .xlsx para visualizar os dados.
					</div>
				</v-card-text>

				<v-card-actions class="px-6 pb-6 pt-4">
					<v-spacer />
					<v-btn text :disabled="saving" @click="fecharDialog">Cancelar</v-btn>
					<v-btn class="financeiro-save-btn" depressed :loading="saving" :disabled="!canSave || saving" @click="salvarLancamentos">
						Salvar lançamentos
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
	</div>
</template>

<script>
import * as XLSX from 'xlsx';
import financeiroServices from './financeiroServices';
import {
	formatCurrencyFieldValue,
	formatCurrencyInputValue,
	hasCurrencyValue,
	parseCurrency,
} from './financeiroCurrency';

const TODAY = new Date().toISOString().slice(0, 10);

function createRowId(index) {
	return `${Date.now()}-${index}`;
}

function normalizeHeader(value) {
	return String(value || '')
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '')
		.trim()
		.toLowerCase()
		.replace(/\s+/g, '_');
}

export default {
	name: 'MultiplosFinanceiroDialog',

	props: {
		value: {
			type: Boolean,
			default: false,
		},
		categorias: {
			type: Array,
			default: () => [],
		},
		contasBancarias: {
			type: Array,
			default: () => [],
		},
		veiculos: {
			type: Array,
			default: () => [],
		},
	},

	data() {
		return {
			dialogOpen: false,
			saving: false,
			arquivo: null,
			tiposLancamento: ['Receber', 'Pagar'],
			linhasImportadas: [],
			headers: [
				{ text: 'Descrição', value: 'descricao', sortable: false, width: '220px' },
				{ text: 'Valor', value: 'valor', sortable: false, width: '150px' },
				{ text: 'Data prevista', value: 'data_prevista', sortable: false, width: '160px' },
				{ text: 'Tipo', value: 'tipo', sortable: false, width: '140px' },
				{ text: 'Categoria', value: 'categoria', sortable: false, width: '180px' },
				{ text: 'Conta bancária', value: 'conta_bancaria_id', sortable: false, width: '190px' },
				{ text: 'Veículos', value: 'veiculo_ids', sortable: false, width: '240px' },
				{ text: 'Observações', value: 'observacoes', sortable: false, width: '180px' },
				{ text: 'Pendências', value: 'erros', sortable: false, width: '220px' },
			],
		};
	},

	computed: {
		invalidRows() {
			return this.linhasImportadas.filter((item) => this.getRowErrors(item).length > 0);
		},

		canSave() {
			return this.linhasImportadas.length > 0 && this.invalidRows.length === 0;
		},

			veiculosOptions() {
				return this.veiculos.map((item) => ({
					label: `${item.placa} - ${item.modelo || 'Sem modelo'}`,
					value: item.id,
				}));
			},
	},

	watch: {
		value: {
			immediate: true,
			handler(newValue) {
				this.dialogOpen = newValue;
			},
		},

		dialogOpen(newValue) {
			this.$emit('input', newValue);
			if (!newValue && !this.saving) {
				this.resetState();
			}
		},
	},

	methods: {
		resetState() {
			this.arquivo = null;
			this.linhasImportadas = [];
		},

		fecharDialog() {
			if (!this.saving) {
				this.dialogOpen = false;
			}
		},

		normalizeDateInput(value) {
			if (!value) {
				return TODAY;
			}

			if (value instanceof Date && !Number.isNaN(value.getTime())) {
				return value.toISOString().slice(0, 10);
			}

			if (typeof value === 'number' && Number.isFinite(value)) {
				const parsed = XLSX.SSF.parse_date_code(value);
				if (parsed) {
					const month = String(parsed.m).padStart(2, '0');
					const day = String(parsed.d).padStart(2, '0');
					return `${parsed.y}-${month}-${day}`;
				}
			}

			const normalized = String(value).trim();
			if (/^\d{4}-\d{2}-\d{2}$/.test(normalized)) {
				return normalized;
			}
			if (/^\d{4}-\d{2}-\d{2}T/.test(normalized) || /^\d{4}-\d{2}-\d{2}\s/.test(normalized)) {
				return normalized.slice(0, 10);
			}
			if (/^\d{2}\/\d{2}\/\d{4}$/.test(normalized)) {
				const [day, month, year] = normalized.split('/');
				return `${year}-${month}-${day}`;
			}

			const date = new Date(normalized);
			return Number.isNaN(date.getTime()) ? TODAY : date.toISOString().slice(0, 10);
		},

		onValorInput(item, value) {
			item.valor = formatCurrencyInputValue(value);
		},

		getRowErrors(item) {
			const errors = [];
			const valor = parseCurrency(item.valor);
			const hasValor = hasCurrencyValue(item.valor);

			if (!String(item.descricao || '').trim()) {
				errors.push('Descrição obrigatória');
			}
			if (!hasValor || !Number.isFinite(valor)) {
				errors.push('Valor obrigatório');
			}
			if (!String(item.tipo || '').trim()) {
				errors.push('Tipo obrigatório');
			}
			if (!String(item.categoria || '').trim()) {
				errors.push('Categoria obrigatória');
			}

			return errors;
		},

		fieldErrorClass(item, field) {
			const errors = this.getRowErrors(item);
			if (field === 'descricao' && errors.includes('Descrição obrigatória')) return 'financeiro-input--error';
			if (field === 'valor' && errors.includes('Valor obrigatório')) return 'financeiro-input--error';
			if (field === 'tipo' && errors.includes('Tipo obrigatório')) return 'financeiro-input--error';
			if (field === 'categoria' && errors.includes('Categoria obrigatória')) return 'financeiro-input--error';
			return '';
		},

		mapImportedRows(rows) {
			return rows
				.map((row, index) => {
					const normalizedEntries = Object.entries(row).reduce((acc, [key, value]) => {
						acc[normalizeHeader(key)] = value;
						return acc;
					}, {});

					const descricao = normalizedEntries.descricao || normalizedEntries.descricao_lancamento || normalizedEntries.historico || '';
					const valor = normalizedEntries.valor || normalizedEntries.valor_lancamento || '';
					const dataPrevista = normalizedEntries.data_prevista || normalizedEntries.data_prevista_pagamento || normalizedEntries.vencimento || normalizedEntries.data || TODAY;
					const observacoes = normalizedEntries.observacoes || normalizedEntries.observacao || normalizedEntries.obs || '';

					const hasSomeValue = [descricao, valor, dataPrevista, observacoes].some((value) => String(value || '').trim() !== '');
					if (!hasSomeValue) {
						return null;
					}

					return {
						__id: createRowId(index),
						descricao: String(descricao || '').trim(),
						valor: formatCurrencyFieldValue(valor),
						data_prevista: this.normalizeDateInput(dataPrevista),
						tipo: '',
						categoria: '',
						conta_bancaria_id: null,
						veiculo_ids: [],
						observacoes: String(observacoes || '').trim(),
					};
				})
				.filter(Boolean);
		},

		handleArquivoSelecionado(file) {
			if (!file) {
				this.linhasImportadas = [];
				return;
			}

			const reader = new FileReader();
			reader.onload = (event) => {
				try {
					const workbook = XLSX.read(event.target.result, {
						type: 'array',
						cellDates: true,
						raw: false,
						dateNF: 'yyyy-mm-dd',
					});
					const sheetName = workbook.SheetNames[0];
					const sheet = workbook.Sheets[sheetName];
					const rows = XLSX.utils.sheet_to_json(sheet, {
						defval: '',
						raw: false,
					});

					this.linhasImportadas = this.mapImportedRows(rows);

					if (!this.linhasImportadas.length && this.$toasted) {
						this.$toasted.error('Nenhum lançamento válido foi encontrado no arquivo importado');
					}
				} catch (error) {
					this.linhasImportadas = [];
					if (this.$toasted) {
						this.$toasted.error('Nao foi possivel ler o arquivo .xlsx informado');
					}
				}
			};

			reader.readAsArrayBuffer(file);
		},

		baixarModeloArquivo() {
			const worksheet = XLSX.utils.json_to_sheet([
				{
					descricao: '',
					valor: '',
					data_prevista: TODAY,
					observacoes: '',
				},
			]);

			const workbook = XLSX.utils.book_new();
			XLSX.utils.book_append_sheet(workbook, worksheet, 'Lancamentos');
			XLSX.writeFile(workbook, 'modelo-importacao-financeiro.xlsx');
		},

		async salvarLancamentos() {
			if (!this.canSave) {
				if (this.$toasted) {
					this.$toasted.error('Preencha os campos obrigatórios antes de salvar os lançamentos');
				}
				return;
			}

			this.saving = true;

			try {
				for (const item of this.linhasImportadas) {
					await financeiroServices.criar({
						descricao: item.descricao,
						valor: parseCurrency(item.valor),
						data_prevista: item.data_prevista || TODAY,
						tipo: item.tipo,
						categoria: item.categoria,
						conta_bancaria_id: item.conta_bancaria_id || null,
						veiculo_ids: Array.isArray(item.veiculo_ids) ? [...item.veiculo_ids] : [],
						observacoes: item.observacoes || null,
					});
				}

				this.$emit('saved');
				this.dialogOpen = false;
				if (this.$toasted) {
					this.$toasted.show('Lançamentos importados com sucesso');
				}
			} catch (error) {
				const errorMessage = error?.response?.data?.error || 'Nao foi possivel importar os lançamentos';
				if (this.$toasted) {
					this.$toasted.error(errorMessage);
				}
			} finally {
				this.saving = false;
			}
		},
	},
};
</script>

<style scoped>
@import "../../assets/inter-font.css";

.financeiro-dialog {
	border-radius: 24px;
	font-family: "Inter", sans-serif;
	border: 1px solid #e6ebf1 !important;
	box-shadow: 0 24px 70px rgba(15, 23, 42, 0.12) !important;
}

.financeiro-dialog__title {
	padding: 24px 24px 18px;
	font-size: 20px;
	font-weight: 700;
	color: #1f2a44;
}

.financeiro-info-box {
	border: 1px solid #dbe7f3;
	border-radius: 16px;
	background: #f8fbff;
	color: #516174;
	font-size: 14px;
	line-height: 1.6;
	padding: 16px 18px;
}

.financeiro-multiplos__actions {
	display: flex;
	align-items: flex-start;
	gap: 14px;
	flex-wrap: wrap;
}

.financeiro-multiplos__upload {
	min-width: 320px;
	flex: 1 1 420px;
}

.financeiro-multiplos__title {
	font-size: 15px;
	font-weight: 800;
	color: #1f2a44;
}

.financeiro-multiplos__subtitle {
	font-size: 13px;
	color: #7a8ca6;
	margin-top: 4px;
}

.financeiro-multiplos__errors {
	display: flex;
	gap: 6px;
	flex-wrap: wrap;
}

.financeiro-error-chip {
	display: inline-flex;
	align-items: center;
	padding: 4px 8px;
	border-radius: 999px;
	font-size: 11px;
	font-weight: 700;
	background: rgba(239, 68, 68, 0.12);
	color: #dc2626;
}

.financeiro-empty-state {
	border: 1px dashed #cbd5e1;
	border-radius: 16px;
	padding: 20px;
	font-size: 14px;
	color: #64748b;
	text-align: center;
	background: #fff;
}

.financeiro-secondary-btn {
	border-color: #10b981 !important;
	color: #10b981 !important;
	font-weight: 700;
	border-radius: 14px;
	text-transform: none;
	padding: 0 18px !important;
	background: #ffffff !important;
}

.financeiro-save-btn {
	background: linear-gradient(135deg, #18c58b 0%, #12b57a 100%) !important;
	color: #fff !important;
	font-weight: 700;
	border-radius: 12px;
	text-transform: none;
	padding: 0 18px !important;
	box-shadow: 0 12px 28px rgba(18, 181, 122, 0.22) !important;
}

.financeiro-table::v-deep thead th {
	color: #7a8ca6 !important;
	font-weight: 800 !important;
	font-size: 11px !important;
	letter-spacing: 0.08em;
	background: #f8fafc;
	border-bottom: 1px solid #e6ebf1 !important;
}

.financeiro-table::v-deep tbody td {
	border-bottom: 1px solid rgba(230, 235, 241, 0.9) !important;
	font-size: 13px;
	color: #1f2a44;
	padding-top: 10px !important;
	padding-bottom: 10px !important;
	vertical-align: top;
}

.financeiro-input--error::v-deep .v-input__slot {
	border-color: #ef4444 !important;
}

@media (max-width: 960px) {
	.financeiro-multiplos__upload {
		min-width: 100%;
	}
}
</style>
